import React from "react";
import { HiCheck } from "react-icons/hi";
import { TrainingLog, PerformanceType } from "../../../types/post";
import {
  PeakEvent,
  eventLastDay,
  eventTypeLabel,
} from "../../../types/event";
import { EventChoice } from "./performanceDraft";

const formatDateID = (id: string): string => {
  if (!id || id.length < 8) return "";
  const d = new Date(
    Number(id.slice(0, 4)),
    Number(id.slice(4, 6)) - 1,
    Number(id.slice(6, 8)),
  );
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const eventSublabel = (e: PeakEvent): string => {
  const last = eventLastDay(e);
  const range =
    last !== e.date
      ? `${formatDateID(e.date)} – ${formatDateID(last)}`
      : formatDateID(e.date);
  // Coach tags ride along on the (truncating) sublabel — first two only.
  const tags = e.tags?.slice(0, 2).join(", ");
  return `${eventTypeLabel(e.type)} · ${range}${tags ? ` · ${tags}` : ""}`;
};

const Chip: React.FC<{
  selected: boolean;
  onClick: () => void;
  label: string;
  sublabel?: string;
  badge?: string;
}> = ({ selected, onClick, label, sublabel, badge }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    className={`w-full text-left text-sm py-2.5 px-3 rounded-xl border flex items-center justify-between gap-2 transition-colors ${
      selected
        ? "border-neutral bg-neutral/10"
        : "border-base-300 bg-base-100 hover:border-base-content/40"
    }`}
  >
    <span className="min-w-0">
      <span className="block truncate font-medium">{label}</span>
      {sublabel && (
        <span className="block truncate text-xs text-base-content/50">
          {sublabel}
        </span>
      )}
    </span>
    <span className="flex items-center gap-2 flex-shrink-0">
      {badge && (
        <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-base-200 text-base-content/60 font-geist">
          {badge}
        </span>
      )}
      {selected && <HiCheck className="w-4 h-4 text-neutral" />}
    </span>
  </button>
);

interface LinkStepProps {
  /** Events spanning the log date. */
  events: PeakEvent[];
  /** Pool for the "Different event" picker (upcoming + recent). */
  pickerEvents: PeakEvent[];
  trainingLogs: (TrainingLog & { id: string })[];
  trainingLogLabel: (log: TrainingLog & { id: string }) => string;
  eventChoice: EventChoice;
  /** True while "Different event" is expanded. */
  pickerOpen: boolean;
  /** True while "Create event" is expanded. */
  createOpen: boolean;
  /** Intent chosen in step 1 — becomes the quick-created event's type. */
  performanceType: PerformanceType | null;
  /** Log date, YYYYMMDD — becomes the quick-created event's date. */
  dateID: string;
  upgradeLogId: string | null;
  createName: string;
  onChooseEvent: (choice: EventChoice) => void;
  onTogglePicker: () => void;
  onToggleCreate: () => void;
  onCreateNameChange: (name: string) => void;
  onChooseUpgrade: (logId: string | null) => void;
}

/**
 * Step 2 — optionally link the performance to an event (spanning the date,
 * a different event, or a quick-created one) and/or upgrade a plain training
 * log of the day (PerformanceLog.md §3).
 */
const LinkStep: React.FC<LinkStepProps> = ({
  events,
  pickerEvents,
  trainingLogs,
  trainingLogLabel,
  eventChoice,
  pickerOpen,
  createOpen,
  performanceType,
  dateID,
  upgradeLogId,
  createName,
  onChooseEvent,
  onTogglePicker,
  onToggleCreate,
  onCreateNameChange,
  onChooseUpgrade,
}) => {
  const selectedEventId =
    eventChoice.kind === "event" ? eventChoice.eventId : null;
  const spanningIds = new Set(events.map((e) => e.id));
  const selectedElsewhere =
    selectedEventId && !spanningIds.has(selectedEventId)
      ? pickerEvents.find((e) => e.id === selectedEventId)
      : undefined;

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-bold font-grotesk text-base-content">
          Is this part of an event?
        </h3>
        <p className="text-sm text-base-content/60 font-grotesk">
          Linking shows it on the event and seeds any session targets.
        </p>
      </div>

      {events.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-base-content/50 font-grotesk">
            Events on {formatDateID(dateID)}
          </p>
          {events.map((e) => (
            <Chip
              key={e.id}
              selected={selectedEventId === e.id}
              onClick={() => onChooseEvent({ kind: "event", eventId: e.id })}
              label={`Is this ${e.name}?`}
              sublabel={eventSublabel(e)}
            />
          ))}
        </div>
      )}

      <div className="space-y-2">
        <p className="text-xs font-medium text-base-content/50 font-grotesk">
          {events.length > 0 ? "Or" : "Event"}
        </p>

        {selectedElsewhere && (
          <Chip
            selected
            onClick={() => onChooseEvent({ kind: "event", eventId: selectedElsewhere.id })}
            label={selectedElsewhere.name}
            sublabel={eventSublabel(selectedElsewhere)}
          />
        )}

        <Chip
          selected={pickerOpen}
          onClick={onTogglePicker}
          label="Different event"
          sublabel="Pick from your upcoming and recent events"
        />
        {pickerOpen && (
          <div className="space-y-2 pl-2 border-l-2 border-base-300">
            {pickerEvents.length === 0 ? (
              <p className="text-sm text-base-content/60 font-grotesk py-1">
                No other events yet. Use Create event instead.
              </p>
            ) : (
              pickerEvents.map((e) => (
                <Chip
                  key={e.id}
                  selected={selectedEventId === e.id}
                  onClick={() => onChooseEvent({ kind: "event", eventId: e.id })}
                  label={e.name}
                  sublabel={eventSublabel(e)}
                />
              ))
            )}
          </div>
        )}

        <Chip
          selected={createOpen || eventChoice.kind === "create"}
          onClick={onToggleCreate}
          label="Create event"
          sublabel={`New ${eventTypeLabel(performanceType ?? undefined).toLowerCase()} on ${formatDateID(dateID)}`}
        />
        {createOpen && (
          <input
            type="text"
            autoFocus
            value={createName}
            onChange={(e) => onCreateNameChange(e.target.value)}
            placeholder="Event name, e.g. State Championships"
            className="w-full px-4 py-3 rounded-md bg-base-200 border border-base-300 focus:border-base-300 focus:outline-none focus:ring-0 text-[16px] font-medium placeholder:text-base-content/40"
          />
        )}

        <Chip
          selected={eventChoice.kind === "none" && !pickerOpen && !createOpen}
          onClick={() => onChooseEvent({ kind: "none" })}
          label="No event"
        />
      </div>

      {trainingLogs.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-base-content/50 font-grotesk">
            Already logged today
          </p>
          {trainingLogs.map((log) => (
            <Chip
              key={log.id}
              selected={upgradeLogId === log.id}
              onClick={() =>
                onChooseUpgrade(upgradeLogId === log.id ? null : log.id)
              }
              label={`Is this ${trainingLogLabel(log)}?`}
              sublabel="Mark this training log as the performance (keeps its load)"
              badge="Upgrade"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default LinkStep;
